import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { User, MapPin, Users, ScanLine, Download, RefreshCw, Database, ChevronRight, Info, Trash2, ScrollText, Baby, Truck } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const isVet = user?.role === "vet";
  const groups = [
    {
      title: "Cuenta",
      items: [
        { to: "/ajustes/perfil", icon: User, label: "Mi perfil", testId: "settings-profile", hint: "Nombre, email, contraseña" },
        ...(isOwner ? [{ to: "/ajustes/usuarios", icon: Users, label: "Usuarios", testId: "settings-users", hint: "Encargados, veterinarios, invitaciones y permisos" }] : []),
      ],
    },
    {
      title: "Datos",
      items: [
        ...(isOwner ? [{ to: "/fincas", icon: MapPin, label: "Fincas y lotes", testId: "settings-farms", hint: "Establecimientos y subdivisiones" }] : []),
        ...((isOwner || isVet) ? [{ to: "/nacimientos-previstos", icon: Baby, label: "Nacimientos previstos", testId: "settings-predicted-births", hint: "Reporte de palpaciones con preñez" }] : []),
        ...(!isVet ? [{ to: "/despachos", icon: Truck, label: "Ventas y despachos", testId: "settings-dispatches", hint: "Informe de animales vendidos y trasladados" }] : []),
        ...(isOwner ? [{ to: "/ajustes/exportar", icon: Download, label: "Exportar datos", testId: "settings-export", hint: "CSV · JSON · PDF" }] : []),
        ...(isOwner ? [{ to: "/ajustes/backups", icon: Database, label: "Backups", testId: "settings-backups", hint: "Snapshots diarios" }] : []),
        ...(isOwner ? [{ to: "/ajustes/papelera", icon: Trash2, label: "Papelera", testId: "settings-trash", hint: "Animales eliminados · 30 días para restaurar" }] : []),
      ],
    },
    {
      title: "Hardware",
      items: [
        { to: "/ajustes/lector", icon: ScanLine, label: "Lector RFID", testId: "settings-reader", hint: "Lector físico o simulador" },
      ],
    },
    {
      title: "Sistema",
      items: [
        { to: "/ajustes/sync", icon: RefreshCw, label: "Sincronización", testId: "settings-sync", hint: "Cola, conflictos, histórico" },
        ...(isOwner ? [{ to: "/ajustes/auditoria", icon: ScrollText, label: "Historial de auditoría", testId: "settings-audit", hint: "Quién hizo cada cambio, con filtros" }] : []),
        { to: "/ajustes/acerca", icon: Info, label: "Acerca de", testId: "settings-about", hint: "Versión y soporte" },
      ],
    },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="space-y-5">
      <div>
        <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Configuración</div>
        <h1 className="font-editorial text-[32px] sm:text-[40px] leading-[1.05] tracking-tight mt-0.5">Ajustes</h1>
        <p className="text-[13px] text-muted-foreground mt-1">Gestioná tu cuenta, fincas, lector y datos.</p>
      </div>
      {groups.map((g) => (
        <div key={g.title} className="space-y-2">
          <div className="px-1 text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">{g.title}</div>
          <Card>
            <CardContent className="p-0">
              <div className="divide-hairline">
                {g.items.map((it) => (
                  <Link
                    key={it.to}
                    to={it.to}
                    className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted transition-colors duration-150 ring-focus"
                    data-testid={it.testId}
                  >
                    <div className="w-9 h-9 rounded-[calc(var(--radius)-0.35rem)] bg-accent text-accent-foreground grid place-items-center shrink-0">
                      <it.icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-heading font-semibold text-foreground tracking-tight">{it.label}</div>
                      {it.hint && <div className="text-[11.5px] text-muted-foreground mt-0.5">{it.hint}</div>}
                    </div>
                    <ChevronRight size={16} className="text-muted-foreground/60" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}
